import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ImageIcon } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: '小A',
    role: '电商设计师',
    stats: { time: '入驻3个月接单量', count: '18单', money: '¥32,600' },
    avg: '平均每单 ¥1,811',
    desc: '入驻3个月，接18单，总收入32600元。',
    detailDesc: '小A擅长各类电商首页、详情页设计，风格多变。入驻平台后，通过AI工具快速产出多套方案，深受甲方喜爱。',
    tags: ['电商设计', 'C4D', 'AI辅助'],
    color: 'from-orange-500/20 to-red-500/5',
    border: 'border-orange-500/20',
    images: [1, 2, 3] // Mock image count
  },
  {
    id: 2,
    name: '老K',
    role: '品牌设计师',
    stats: { time: '长期合作企业', count: '2家', money: '¥22,000' },
    avg: '月均稳定收入6500元',
    desc: '长期合作2家企业，月均稳定收入6500元。平均每单 ¥11205',
    detailDesc: '老K拥有10年平面设计经验，专注于VI升级与品牌全案。利用平台对接了两家长期稳定的企业客户，实现了自由职业的稳定收入。',
    tags: ['品牌VI', '平面设计', '包装设计'],
    color: 'from-blue-500/20 to-cyan-500/5',
    border: 'border-blue-500/20',
    images: [1, 2, 3, 4]
  },
  {
    id: 3,
    name: '阿佐',
    role: 'AI生图专家',
    stats: { time: '本月接单量', count: '10单', money: '¥18,800' },
    avg: '靠AI设计技能月接10单',
    desc: '靠AI设计技能，月接10单，收入18800元。平均每单 ¥1,880',
    detailDesc: '阿佐是平台首批AI做图专家，擅长Midjourney与Stable Diffusion。不仅作图速度快，且画面精细度极高，单月接单量屡创新高。',
    tags: ['Midjourney', 'SD', 'AI合成修图'],
    color: 'from-purple-500/20 to-pink-500/5',
    border: 'border-purple-500/20',
    images: [1, 2]
  }
];

const Testimonials: React.FC = () => {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const selectedTestimonial = testimonials.find(t => t.id === selectedId);

  return (
    <section id="cases" className="bg-black py-16 md:py-24 px-4 relative">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl md:text-5xl font-semibold text-white mb-8 md:mb-16 text-center">
          ＞800元 平均每单费用<br/>
          <span className="text-gray-500 text-sm md:text-3xl font-normal mt-2 md:mt-4 block">全是高质量商单，接一单就能赚</span>
        </h2>

        <div className="grid grid-cols-3 gap-2 md:gap-8">
          {testimonials.map((item, index) => (
            <motion.div
              key={item.id}
              layoutId={`card-${item.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              // 12.1暂时隐藏对话框
              // onClick={() => setSelectedId(item.id)}
              className={`p-px rounded-xl md:rounded-[2rem] bg-gradient-to-b ${item.color} cursor-pointer group hover:scale-[1.02] transition-transform duration-300`}
            >
              <div className="h-full bg-[#0a0a0a] rounded-xl md:rounded-[2rem] p-3 md:p-8 flex flex-col relative overflow-hidden group-hover:bg-[#111] transition-colors">
                <div className={`absolute top-0 right-0 p-24 ${item.color.split(' ')[0]} blur-[80px] opacity-20`} />
                
                <div className="relative z-10 flex flex-col h-full">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 mb-3 md:mb-8">
                        <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-gray-800 flex items-center justify-center text-xs md:text-lg font-bold shrink-0">
                            {item.name[0]}
                        </div>
                        <div className="min-w-0">
                            <div className="font-bold text-white text-[10px] md:text-base truncate">@{item.name}</div>
                            <div className="text-[8px] md:text-sm text-gray-500 truncate">{item.role}</div>
                        </div>
                    </div>

                    <div className="space-y-2 md:space-y-4 mb-4 md:mb-12 flex-grow">
                        <div className="flex flex-col md:flex-row justify-between md:items-end border-b border-white/10 pb-2 md:pb-4 gap-1">
                            <span className="text-gray-400 text-[8px] md:text-sm truncate">{item.stats.time}</span>
                            <span className="text-white font-bold text-xs md:text-base">{item.stats.count}</span>
                        </div>
                        <div className="flex flex-col md:flex-row justify-between md:items-end gap-1">
                            <span className="text-gray-400 text-[8px] md:text-sm">总收入</span>
                            <span className="text-sm md:text-2xl font-bold text-white truncate">{item.stats.money}</span>
                        </div>
                    </div>

                    <div className="mt-auto flex justify-between items-end">
                        <div className="text-[8px] md:text-sm text-gray-300 font-medium mb-1 truncate max-w-[80%]">{item.avg}</div>
                         <div className="bg-white/10 p-1 md:p-2 rounded-full opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                             <ArrowRight className="text-white w-3 h-3 md:w-4 md:h-4"/>
                         </div>
                    </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedId && selectedTestimonial && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
             <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }}
                onClick={() => setSelectedId(null)}
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
             />
             <motion.div 
                layoutId={`card-${selectedId}`}
                className="w-full max-w-2xl bg-[#111] border border-white/10 rounded-2xl md:rounded-3xl overflow-hidden relative z-10 max-h-[90vh] overflow-y-auto scrollbar-hide"
             >
                {/* Close Button */}
                <button 
                  onClick={(e) => { e.stopPropagation(); setSelectedId(null); }}
                  className="absolute top-2 right-2 md:top-4 md:right-4 p-1.5 md:p-2 bg-black/50 rounded-full hover:bg-black/80 transition-colors z-20 border border-white/10"
                >
                  <X className="text-white w-4 h-4 md:w-5 md:h-5" />
                </button>

                {/* Header Section */}
                <div className={`p-5 md:p-8 bg-gradient-to-b ${selectedTestimonial.color.replace('/20', '/10')} relative`}>
                    <div className="flex items-center gap-4 md:gap-6 relative z-10">
                        <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-gray-800 flex items-center justify-center text-xl md:text-3xl font-bold text-white shadow-2xl border-2 border-white/10 shrink-0">
                            {selectedTestimonial.name[0]}
                        </div>
                        <div>
                            <h3 className="text-lg md:text-2xl font-bold text-white mb-0.5 md:mb-1">设计师 @{selectedTestimonial.name}</h3>
                            <p className="text-xs md:text-base text-gray-300">{selectedTestimonial.role}</p>
                            <div className="flex flex-wrap gap-1.5 md:gap-2 mt-2 md:mt-3">
                                {selectedTestimonial.tags.map(tag => (
                                    <span key={tag} className="text-[10px] md:text-xs px-1.5 py-0.5 md:px-2 md:py-1 rounded-md bg-white/10 text-gray-300 border border-white/5">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="p-5 md:p-8">
                     {/* Stats Row */}
                    <div className="mb-6 md:mb-8">
                        <h4 className="text-base md:text-lg font-bold text-white mb-2 md:mb-3">经验分享</h4>
                        <p className="text-xs md:text-base text-gray-400 leading-relaxed">
                            {selectedTestimonial.detailDesc}
                        </p>
                    </div>

                    <div>
                        <h4 className="text-base md:text-lg font-bold text-white mb-3 md:mb-4">部分案例展示</h4>
                        <div className="grid grid-cols-2 gap-2 md:gap-4">
                           {selectedTestimonial.images.map((_, i) => (
                               <div key={i} className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center border border-white/5 hover:border-white/20 transition-colors group/img">
                                   <ImageIcon className="text-gray-600 w-6 h-6 md:w-8 md:h-8 group-hover/img:text-gray-400 transition-colors" />
                               </div>
                           ))}
                        </div>
                    </div>
                </div>
             </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Testimonials;